import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import { SchemeForm, SchemeFormSchema, TrainingType } from '@strength/domain';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../../components/drawer';
import { Form } from '../../../../components/form/form';
import { FocusOption } from '../types';
import { SchemeSheetContent } from './SchemeSheetContent';

type CreateSchemeSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (scheme: SchemeForm) => void;
  focusOptions: FocusOption[];
  trainingTypeOptions: TrainingType[];
};

export const CreateSchemeSheet = ({
  open,
  onOpenChange,
  onSubmit,
  focusOptions,
  trainingTypeOptions,
}: CreateSchemeSheetProps) => {
  const { t } = useTranslation();

  const form = useForm<SchemeForm>({
    resolver: zodResolver(SchemeFormSchema),
    defaultValues: createDefaultSchemeForm(),
    mode: 'onChange',
  });
  const { handleSubmit, reset } = form;

  const onFormSubmit = (scheme: SchemeForm) => {
    onSubmit(scheme);
    onOpenChange(false);
    reset();
  };

  return (
    <Drawer open={open} onClose={() => onOpenChange(false)}>
      <DrawerHeader
        title={t('createScheme')}
        onClose={() => onOpenChange(false)}
      />

      <Form {...form}>
        <form
          id="create-scheme-form"
          className="gap-4 flex flex-col overflow-y-hidden flex-grow p-1"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div className="px-8 py-4 grow">
            <SchemeSheetContent
              form={form}
              focusOptions={focusOptions}
              trainingTypeOptions={trainingTypeOptions}
              isEdit={false}
            />
          </div>
        </form>
      </Form>
      <DrawerFooter className="flex flex-row gap-2 w-full p-8 justify-end">
        <Button variant="outlined" onClick={() => onOpenChange(false)}>
          {t('cancel')}
        </Button>
        <Button type="submit" form="create-scheme-form">
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

const createDefaultSchemeForm = (): Partial<SchemeForm> => ({
  focusIds: [],
  metrics: [],
  name: '',
  trainingTypeId: '',
  workouts: [],
});
